<template>
  <div class="dw act on">
    <div class="dwh">
      <div class="dwt">Activity</div>
      <span class="grow"></span>
      <button type="button" class="dwx" @click="$emit('close')">✕</button>
    </div>
    <div class="act-filters">
      <input class="act-search" v-model.trim="q" type="text" placeholder="Search…">
      <div class="act-chips">
        <button type="button" class="act-chip" :class="{ on: filter === '' }" @click="filter = ''">All</button>
        <button type="button" class="act-chip" :class="{ on: filter === 'smart_link' }" @click="filter = 'smart_link'">Smart link</button>
        <button type="button" class="act-chip" :class="{ on: filter === 'ad_set' }" @click="filter = 'ad_set'">Ad sets</button>
        <button type="button" class="act-chip" :class="{ on: filter === 'offer' }" @click="filter = 'offer'">Offers</button>
      </div>
    </div>
    <div class="dwb act-body">
      <div v-if="loading && !items.length" class="act-empty">Loading…</div>
      <div v-else-if="!visible.length" class="act-empty">{{ q ? 'No matches' : 'No activity yet' }}</div>
      <div v-for="row in visible" :key="row.id" class="act-item">
        <div class="act-meta">
          <span class="act-badge" :class="row.entity_type">{{ entityLabel(row.entity_type) }}</span>
          <span class="act-action">{{ actionLabel(row.action) }}</span>
          <span class="act-time">{{ fmtTime(row.created_at) }}</span>
        </div>
        <div class="act-who">
          <b>{{ row.user_name || 'unknown' }}</b>
          <span v-if="row.entity_name">{{ row.entity_name }}</span>
          <span class="act-id">#{{ row.entity_id }}</span>
        </div>
        <div v-if="globalOfferId(row)" class="act-go">G.O - {{ globalOfferId(row) }}</div>
        <div v-if="changeList(row).length" class="act-changes">
          <div v-for="c in changeList(row)" :key="c.field" class="act-ch">
            <span class="act-field">{{ fieldLabel(c.field) }}</span>
            <span class="act-from">{{ fmtVal(c.from) }}</span>
            <span class="act-arrow">→</span>
            <span class="act-to">{{ fmtVal(c.to) }}</span>
          </div>
        </div>
      </div>
      <button
        v-if="items.length < total"
        class="btn act-more"
        type="button"
        :disabled="loading"
        @click="loadMore"
      >Load more</button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

const FIELD_LABELS = {
  name: 'Name',
  description: 'Description',
  frequency: 'Frequency',
  ip_frequency: 'IP Frequency',
  ip_frequency_level: 'IP Frequency Level',
  fallback_url: 'Fallback URL',
  ad_set_order: 'Ad set order',
  group_id: 'Group',
  status: 'Status',
  forced: 'Forced',
  geo: 'Geo',
  region: 'Region',
  os: 'OS',
  clicks_cap: 'Clicks cap',
  comments: 'Notes',
  connection_type: 'Connection type',
  traffic_in_app: 'In-app traffic',
  traffic_mobile: 'Mobile web',
  creative_tag: 'Creative tag',
  creative_img: 'Creative image',
  bundles_lists_id: 'Bundles list',
  use_offer_rotation: 'Offer rotation',
  weight: 'Weight',
  auto_weight_enabled: 'Auto weight',
  cap: 'Cap',
  delivery_frequency: 'Delivery frequency',
  click_ctr: 'Click CTR',
  os_version: 'OS version',
  is_device_id_traffic: 'Device ID traffic',
  pid: 'PID',
  subs_query: 'Subs query',
  custom_url: 'Custom URL',
  advertiser_requests: 'Advertiser requests',
  paused: 'Paused',
  pause_duration_minutes: 'Pause duration',
  affise_id: 'Affise ID',
  sub_rules: 'Sub rules',
  global_offer_id: 'Global offer',
  source_id: 'Copied from',
  source_name: 'Source name',
  changed: 'Offers changed'
}

const ACTION_LABELS = {
  update: 'Updated',
  create: 'Created',
  delete: 'Deleted',
  duplicate: 'Duplicated',
  assoc: 'Associated',
  auto_weight: 'Auto weight'
}

export default {
  name: 'ActivityDrawer',
  props: {
    campId: { type: [Number, String], required: true }
  },
  data() {
    return {
      q: '',
      filter: '',
      items: [],
      total: 0,
      loading: false
    }
  },
  computed: {
    visible() {
      const type = this.filter
      const needle = (this.q || '').toLowerCase()
      return this.items.filter((row) => {
        if (type && row.entity_type !== type) return false
        if (!needle) return true
        return this.rowText(row).includes(needle)
      })
    }
  },
  watch: {
    campId() {
      this.q = ''
      this.filter = ''
      this.fetch(true)
    }
  },
  created() {
    this.fetch(true)
  },
  methods: {
    async fetch(reset) {
      if (!this.campId) return
      this.loading = true
      try {
        const offset = reset ? 0 : this.items.length
        const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Activity(this.campId), {
          params: { limit: 200, offset }
        })
        const data = (typeof r.json === 'function' ? await r.json() : null) || r.body || r.data || {}
        const next = data.items || []
        this.items = reset ? next : this.items.concat(next)
        this.total = data.total || 0
      } catch (err) {
        this.notifyError('Could not load activity')
      }
      this.loading = false
    },
    loadMore() {
      if (this.loading || this.items.length >= this.total) return
      this.fetch(false)
    },
    rowText(row) {
      const parts = [
        this.entityLabel(row.entity_type),
        this.actionLabel(row.action),
        row.user_name,
        row.entity_name,
        String(row.entity_id),
        this.fmtTime(row.created_at)
      ]
      const goId = this.globalOfferId(row)
      if (goId) parts.push('g.o', 'go', String(goId), 'global offer')
      this.changeList(row).forEach((c) => {
        parts.push(this.fieldLabel(c.field), this.fmtVal(c.from), this.fmtVal(c.to), c.field)
      })
      return parts.filter(Boolean).join(' ').toLowerCase()
    },
    globalOfferId(row) {
      const changes = this.changesOf(row)
      const ch = changes && changes.global_offer_id
      if (!ch) return 0
      const v = (ch.to != null && ch.to !== '') ? ch.to : ch.from
      const n = Number(v)
      return n > 0 ? n : 0
    },
    entityLabel(type) {
      if (type === 'smart_link') return 'Smart link'
      if (type === 'ad_set') return 'Ad set'
      if (type === 'offer') return 'Offer'
      return type
    },
    actionLabel(action) {
      return ACTION_LABELS[action] || action
    },
    fieldLabel(field) {
      return FIELD_LABELS[field] || field
    },
    changesOf(row) {
      let ch = row && row.changes
      if (!ch) return null
      if (typeof ch === 'string') {
        try { ch = JSON.parse(ch) } catch (e) { return null }
      }
      if (typeof ch !== 'object') return null
      return ch
    },
    changeList(row) {
      const changes = this.changesOf(row)
      if (!changes) return []
      return Object.keys(changes)
        .filter((field) => field !== 'global_offer_id' || row.action === 'assoc')
        .map((field) => ({
          field,
          from: changes[field] && changes[field].from,
          to: changes[field] && changes[field].to
        }))
    },
    fmtTime(ts) {
      if (!ts) return ''
      return moment.utc(ts).local().format('YYYY-MM-DD HH:mm')
    },
    fmtVal(v) {
      if (v == null || v === '') return '—'
      if (typeof v === 'boolean') return v ? 'Yes' : 'No'
      if (Array.isArray(v)) {
        if (!v.length) return '—'
        if (v[0] && typeof v[0] === 'object' && v[0].sub) {
          return v.map((r) => {
            const extra = (r.extras && r.extras.length) ? ` +${r.extras.length} extra` : ''
            return `${r.sub} ${r.mode}${extra}`
          }).join(', ')
        }
        return v.join(', ')
      }
      if (typeof v === 'object') return JSON.stringify(v)
      return String(v)
    }
  }
}
</script>
